<template>
  <div class="item-picker">
    <div class="view-body">
      <!-- Search -->
      <div class="add-item-search">
        <base-input class="mb-0">
          <el-input
            v-model="searchQuery"
            placeholder="Search items..."
            prefix-icon="el-icon-search"
            clearable
            size="small"
          />
        </base-input>
      </div>

      <!-- Category chips -->
      <div v-if="categoryChips.length > 1" class="add-item-chips">
        <button
          v-for="chip in categoryChips"
          :key="chip.id == null ? 'all' : chip.id"
          type="button"
          class="add-item-chip"
          :class="{ 'add-item-chip--active': activeCategoryId === chip.id }"
          @click="activeCategoryId = chip.id"
        >
          {{ chip.name }}
        </button>
      </div>

      <!-- Loading skeleton -->
      <div v-if="loadingItems" class="add-item-grid">
        <div v-for="n in 6" :key="'skel-' + n" class="add-item-tile add-item-tile--skel">
          <div class="skel" style="width: 100%; height: 60px;"></div>
          <div class="skel mt-2" style="width: 70%; height: 10px;"></div>
          <div class="skel mt-1" style="width: 40%; height: 10px;"></div>
        </div>
      </div>

      <!-- Tiles grid -->
      <template v-else>
        <div v-if="filteredItems.length === 0 && searchQuery" class="add-item-empty">
          <p class="error-secondary-text mb-0">No items match "{{ searchQuery }}"</p>
        </div>

        <div class="add-item-grid">
          <button
            v-for="item in filteredItems"
            :key="'item-' + item.id"
            type="button"
            class="add-item-tile"
            :class="{
              'add-item-tile--selected': mode === 'catalog' && selectedItem && selectedItem.id === item.id,
              'add-item-tile--disabled': isItemOutOfStock(item)
            }"
            :disabled="isItemOutOfStock(item)"
            @click="selectItem(item)"
          >
            <div class="add-item-tile__media">
              <img
                v-if="getItemImageUrl(item)"
                :src="getItemImageUrl(item)"
                :alt="item.name"
              />
              <i v-else class="tim-icons icon-cart add-item-tile__placeholder"></i>
              <span
                v-if="isItemOutOfStock(item)"
                class="add-item-tile__badge add-item-tile__badge--out"
              >
                Out of stock
              </span>
              <span
                v-if="isRecommended(item)"
                class="add-item-tile__badge add-item-tile__badge--addon"
                :title="gameName ? ('Add-on for ' + gameName) : 'Add-on'"
              >
                Add-on
              </span>
            </div>
            <div class="add-item-tile__name text-white">{{ item.name }}</div>
            <div class="add-item-tile__meta">
              <span class="add-item-tile__price text-white">{{ getAmount(item.price) }}</span>
              <span class="add-item-tile__qty">
                <template v-if="item.unlimited_available_quantity">&infin;</template>
                <template v-else>x{{ item.available }}</template>
              </span>
            </div>
          </button>

          <!-- Custom item tile (always last) -->
          <button
            type="button"
            class="add-item-tile add-item-tile--custom"
            :class="{ 'add-item-tile--selected': mode === 'custom' }"
            @click="selectCustom"
          >
            <div class="add-item-tile__media add-item-tile__media--custom">
              <i class="tim-icons icon-simple-add"></i>
            </div>
            <div class="add-item-tile__name text-white">Custom item</div>
            <div class="add-item-tile__meta">
              <span class="add-item-tile__qty">One-off charge</span>
            </div>
          </button>
        </div>
      </template>

      <!-- Detail row -->
    </div>

    <!-- Selection strip — pinned between the scrolling grid and the footer
         so the configure step stays visible no matter how long the catalog
         is. Without this, a tall grid pushes the qty/price controls below
         the fold and the user can't see what they're about to add. -->
    <div v-if="mode === 'catalog' && selectedItem" class="add-item-strip">
      <div class="add-item-strip__inner">
        <div class="add-item-detail__name text-white">{{ selectedItem.name }}</div>

        <div class="add-item-detail__row">
          <div class="add-item-detail__col add-item-detail__col--qty">
            <label class="add-item-detail__label">Qty</label>
            <el-input-number
              class="add-item-detail__qty-input"
              v-model="quantity"
              :min="1"
              :max="selectedItem.unlimited_available_quantity ? undefined : selectedItem.available"
              size="small"
            />
            <a v-if="partySize > 0" href="#" class="add-item-detail__override-link" @click.prevent="matchPartySize">
              Match party size ({{ partySize }})
            </a>
          </div>

          <div class="add-item-detail__col add-item-detail__col--price">
            <label class="add-item-detail__label">Price</label>
            <div v-if="!priceOverrideActive" class="add-item-detail__price-display">
              <span class="text-white add-item-detail__price-value">{{ getAmount(unitPrice) }}</span>
              <a href="#" class="add-item-detail__override-link" @click.prevent="enablePriceOverride">
                Override
              </a>
            </div>
            <money-input
              v-else
              v-model="unitPrice"
              :currency="currency"
              :currency-symbol="currencySymbol"
              :currency-format="currencyFormat"
              :min="0"
            />
            <a
              v-if="priceOverrideActive && isPriceOverridden"
              href="#"
              class="add-item-detail__override-link"
              @click.prevent="resetPriceOverride"
            >
              Reset to {{ getAmount(originalPrice) }}
              <i class="tim-icons icon-refresh-02 add-item-detail__reset-icon"></i>
            </a>
          </div>

          <div class="add-item-detail__col add-item-detail__col--total">
            <label class="add-item-detail__label">Total</label>
            <div class="text-white add-item-detail__total">{{ getAmount(lineTotal) }}</div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="mode === 'custom'" class="add-item-strip add-item-strip--tall">
      <div class="add-item-strip__inner">
        <div class="add-item-detail__name text-white">Custom item</div>

        <div class="add-item-detail__row add-item-detail__row--stack">
          <div class="add-item-detail__col">
            <base-input
              class="mb-0"
              label="Name"
              placeholder="Purchase name"
              v-model="customName"
              :error="customErrors.name"
            />
          </div>
        </div>

        <div class="add-item-detail__row add-item-detail__row--stack">
          <div class="add-item-detail__col">
            <base-input class="mb-0" label="Description (optional)">
              <el-input
                v-model="customDescription"
                :rows="2"
                type="textarea"
                placeholder="Notes for this charge"
                size="small"
              />
            </base-input>
          </div>
        </div>

        <div class="add-item-detail__row">
          <div class="add-item-detail__col add-item-detail__col--qty">
            <label class="add-item-detail__label">Qty</label>
            <el-input-number
              class="add-item-detail__qty-input"
              v-model="customQuantity"
              :min="1"
              size="small"
            />
            <a v-if="partySize > 0" href="#" class="add-item-detail__override-link" @click.prevent="matchPartySize">
              Match party size ({{ partySize }})
            </a>
            <small v-if="customErrors.quantity" class="add-item-detail__field-error">
              {{ customErrors.quantity }}
            </small>
          </div>

          <div class="add-item-detail__col add-item-detail__col--price">
            <label class="add-item-detail__label">Price per unit</label>
            <money-input
              v-model="customPrice"
              :currency="currency"
              :currency-symbol="currencySymbol"
              :currency-format="currencyFormat"
              :min="0"
            />
            <small v-if="customErrors.price" class="add-item-detail__field-error">
              {{ customErrors.price }}
            </small>
          </div>

          <div class="add-item-detail__col add-item-detail__col--total">
            <label class="add-item-detail__label">Total</label>
            <div class="text-white add-item-detail__total">{{ getAmount(customLineTotal) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Save error sits above the footer so it's always visible too. -->
    <div v-if="displayError" class="add-item-error-bar">
      <i class="tim-icons icon-alert-circle-exc mr-1"></i>
      {{ displayError }}
    </div>

    <!-- Footer -->
    <div class="view-footer">
      <base-button size="sm" type="default" @click="$emit('cancel')">Cancel</base-button>
      <base-button
        size="sm"
        type="primary"
        :loading="saving"
        :disabled="!canSave"
        @click="confirm"
      >
        {{ confirmLabel }}
      </base-button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { Auth } from 'aws-amplify';
import { API_LOCATION } from '@/api-config';
import { formatCurrency } from '@/util/currencyFormat';
import { diacriticIncludes } from '@/util/search';

export default {
  name: 'ItemPicker',

  props: {
    companyId: { type: [String, Number], required: true },
    companyGroupId: { type: [String, Number], required: true },
    // The experience(s) in context — items that are add-ons for any of them get
    // prioritised to the top of the grid and labelled. `gameId` is the single
    // booking case (booking drawer); `gameIds` covers a transaction that spans
    // several bookings (TransactionDetails).
    gameId: { type: [String, Number], default: null },
    gameIds: { type: Array, default: () => [] },
    gameName: { type: String, default: '' },
    currency: { type: String, default: 'USD' },
    currencySymbol: { type: String, default: '$' },
    currencyFormat: { type: Number, default: 0 },
    numberFormat: { type: Number, default: 0 },
    // Players on the order; shows a "Match party size (N)" quick-fill on the
    // quantity when greater than 0.
    partySize: { type: Number, default: 0 },
    confirmLabel: { type: String, default: 'Add to order' },
    // The parent owns the request (post now, or stage for checkout), so it
    // owns the busy flag and the error text.
    saving: { type: Boolean, default: false },
    errorMessage: { type: String, default: '' }
  },

  data() {
    return {
      items: [],
      categories: [],
      loadingItems: false,
      searchQuery: '',
      activeCategoryId: null,
      mode: 'catalog',
      selectedItem: null,
      quantity: 1,
      unitPrice: 0,
      originalPrice: 0,
      priceOverrideActive: false,
      customName: '',
      customDescription: '',
      customPrice: null,
      customQuantity: 1,
      customErrors: { name: '', price: '', quantity: '' },
      loadError: ''
    };
  },

  computed: {
    displayError() {
      return this.errorMessage || this.loadError;
    },
    categoryChips() {
      // Only surface categories that actually have an internally-sellable item,
      // so the gate below never leaves a chip that filters to an empty grid.
      const sellableCatIds = new Set(
        this.items
          .filter(i => !i.is_archived && Number(i.sellable_internal))
          .map(i => Number(i.item_category_id))
      );
      const chips = [{ id: null, name: 'All' }];
      for (const cat of this.categories) {
        if (sellableCatIds.has(Number(cat.id))) {
          chips.push({ id: cat.id, name: cat.name });
        }
      }
      return chips;
    },
    filteredItems() {
      const q = this.searchQuery.trim().toLowerCase();
      const filtered = this.items.filter(item => {
        if (item.is_archived) return false;
        // Only items that can be sold internally (staff console) — not-for-sale
        // props and booking-site-only items don't belong in the add-to-transaction list.
        if (!Number(item.sellable_internal)) return false;
        if (this.activeCategoryId != null && Number(item.item_category_id) !== Number(this.activeCategoryId)) {
          return false;
        }
        if (q && !diacriticIncludes(item.name, q)) return false;
        return true;
      });
      if (!this.relevantGameIds.length) return filtered;
      // Add-ons for the in-context experience(s) float to the top; sort is
      // stable, so everything else keeps its original order.
      return filtered.slice().sort((a, b) =>
        (this.isRecommended(a) ? 0 : 1) - (this.isRecommended(b) ? 0 : 1)
      );
    },
    relevantGameIds() {
      // Union of the single gameId and the gameIds list, as numbers.
      const ids = [];
      if (this.gameId != null && this.gameId !== '') ids.push(Number(this.gameId));
      for (const g of (this.gameIds || [])) {
        const n = Number(g);
        if (!isNaN(n) && !ids.includes(n)) ids.push(n);
      }
      return ids;
    },
    lineTotal() {
      return (parseFloat(this.unitPrice) || 0) * (parseInt(this.quantity, 10) || 0);
    },
    customLineTotal() {
      return (parseFloat(this.customPrice) || 0) * (parseInt(this.customQuantity, 10) || 0);
    },
    // Half-cent tolerance. MoneyInput's precision="2" rounds on first focus,
    // so a 3-decimal legacy price (e.g. 10.005) would flip to 10.01 with no
    // user intent. Anything finer than half a cent is below the cents ledger
    // resolution anyway.
    isPriceOverridden() {
      if (this.unitPrice == null || this.originalPrice == null) return false;
      return Math.abs(parseFloat(this.unitPrice) - parseFloat(this.originalPrice)) > 0.005;
    },
    canSave() {
      if (this.saving) return false;
      if (this.mode === 'catalog') {
        if (!this.selectedItem) return false;
        if (!this.quantity || this.quantity < 1) return false;
        if (!this.selectedItem.unlimited_available_quantity && this.quantity > this.selectedItem.available) {
          return false;
        }
        // MoneyInput emits null when the user clears the override field.
        // Without this guard a cleared field would submit as $0 with
        // price_override=true — silent revenue loss on a happy-path gesture.
        if (this.unitPrice == null || this.unitPrice === '' || parseFloat(this.unitPrice) < 0) return false;
        return true;
      }
      if (this.mode === 'custom') {
        if (!this.customName || !this.customName.trim()) return false;
        if (!this.customQuantity || this.customQuantity < 1) return false;
        // Custom items default to null; require a positive price. A $0
        // custom line is almost always an unfilled form, not a deliberate
        // comp (use a 100%-off promo for that instead).
        if (this.customPrice == null || this.customPrice === '' || parseFloat(this.customPrice) <= 0) return false;
        return true;
      }
      return false;
    }
  },

  watch: {
    // Stale validation errors stick on MoneyInput / base-input until the
    // next save call clears them. Clear inline as the user edits so the
    // field doesn't keep flagging a problem they already fixed.
    customName() { this.customErrors.name = ''; },
    customPrice() { this.customErrors.price = ''; },
    customQuantity() { this.customErrors.quantity = ''; }
  },

  async mounted() {
    await this.loadInventory();
  },

  methods: {
    async getAuthToken() {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    },

    getAmount(value) {
      return formatCurrency(value, {
        currency: this.currency,
        currencySymbol: this.currencySymbol,
        currencyFormat: this.currencyFormat,
        numberFormat: this.numberFormat
      });
    },

    isItemOutOfStock(item) {
      if (item.unlimited_available_quantity) return false;
      return Number(item.available) <= 0;
    },

    isRecommended(item) {
      // True only when the item is actually an add-on (is_addon) AND linked to one
      // of the in-context experiences. The is_addon guard matters because
      // item_addon_event_link rows can linger on non-add-on items (e.g. from the
      // seeded defaults), so addon_events alone would mislabel them.
      if (!Number(item.is_addon)) return false;
      const relevant = this.relevantGameIds;
      if (!relevant.length) return false;
      return (item._addonGameIds || []).some(id => relevant.includes(id));
    },

    getItemImageUrl(item) {
      if (!item || !item.img) return null;
      const img = item._parsedImg;
      if (img && img.url) return img.url;
      return null;
    },

    async loadInventory() {
      this.loadingItems = true;
      this.loadError = '';
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}` +
          `/inventory?count=10000000&offset=0&item_name=&category=&unit_price=&available=&purchased=&internal_only=`;
        const response = await axios.get(url, config);
        const items = (response.data.items || []).map(item => {
          let parsedImg = null;
          if (item.img) {
            try {
              const arr = JSON.parse(item.img);
              parsedImg = Array.isArray(arr) ? arr[0] : arr;
            } catch (e) {
              parsedImg = null;
            }
          }
          // Game ids this item is an add-on for (addon_events comes as a JSON
          // string of { id: game_id, name }). Used to prioritise/label add-ons
          // for the booked experience.
          let addonGameIds = [];
          let ae = item.addon_events;
          if (typeof ae === 'string') {
            try { ae = JSON.parse(ae); } catch (e) { ae = []; }
          }
          if (Array.isArray(ae)) {
            addonGameIds = ae
              .map(e => Number(e && (e.id != null ? e.id : e.game_id)))
              .filter(n => !isNaN(n));
          }
          return { ...item, _parsedImg: parsedImg, _addonGameIds: addonGameIds };
        });
        this.items = items;
        this.categories = response.data.item_categories || [];
      } catch (error) {
        console.error('Failed to load inventory:', error);
        if (error.response?.status === 401) {
          this.$router.push('/forbidden');
          return;
        }
        this.loadError = 'Failed to load items';
      } finally {
        this.loadingItems = false;
      }
    },

    selectItem(item) {
      if (this.isItemOutOfStock(item)) return;
      this.mode = 'catalog';
      this.selectedItem = item;
      this.quantity = 1;
      this.unitPrice = parseFloat(item.price) || 0;
      this.originalPrice = parseFloat(item.price) || 0;
      this.priceOverrideActive = false;
    },

    selectCustom() {
      this.mode = 'custom';
      this.selectedItem = null;
      this.customErrors = { name: '', price: '', quantity: '' };
    },

    enablePriceOverride() {
      this.priceOverrideActive = true;
    },

    resetPriceOverride() {
      this.unitPrice = this.originalPrice;
    },

    validateCustom() {
      this.customErrors = { name: '', price: '', quantity: '' };
      let ok = true;
      if (!this.customName || !this.customName.trim()) {
        this.customErrors.name = 'Name is required';
        ok = false;
      }
      const price = parseFloat(this.customPrice);
      if (this.customPrice === '' || this.customPrice == null || Number.isNaN(price) || price <= 0) {
        this.customErrors.price = 'Enter a price greater than 0';
        ok = false;
      }
      if (!this.customQuantity || this.customQuantity < 1) {
        this.customErrors.quantity = 'Quantity must be at least 1';
        ok = false;
      }
      return ok;
    },

    matchPartySize() {
      const n = parseInt(this.partySize, 10) || 0;
      if (n < 1) return;
      if (this.mode === 'catalog' && this.selectedItem) {
        const cap = this.selectedItem.unlimited_available_quantity ? n : Number(this.selectedItem.available);
        this.quantity = Math.max(1, Math.min(n, cap));
      } else if (this.mode === 'custom') {
        this.customQuantity = n;
      }
    },

    // Emits the line the parent will post or stage. Same payload shape the
    // post-create Add item view has always sent to POST .../transactions/{id}/items.
    confirm() {
      if (!this.canSave) return;

      let payload;
      if (this.mode === 'catalog') {
        const isOverride = this.isPriceOverridden;
        const unit = parseFloat(this.unitPrice) || 0;
        const qty = parseInt(this.quantity, 10) || 0;
        payload = {
          id: this.selectedItem.id,
          name: this.selectedItem.name,
          price: unit,
          quantity: qty,
          available: this.selectedItem.available,
          unlimited_available_quantity: !!this.selectedItem.unlimited_available_quantity,
          price_override: isOverride,
          original_price: this.originalPrice,
          amount: unit * qty
        };
      } else {
        if (!this.validateCustom()) return;
        const unit = parseFloat(this.customPrice) || 0;
        const qty = parseInt(this.customQuantity, 10) || 0;
        payload = {
          name: this.customName.trim(),
          description: this.customDescription || '',
          price: unit,
          quantity: qty,
          amount: unit * qty
        };
      }
      this.$emit('confirm', { ...payload, line_total: payload.amount });
    }
  }
};
</script>

<style scoped lang="scss">
.item-picker {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.view-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;

  // Override the global green-bordered scrollbar (see _sidebar-and-main-panel.scss:572)
  // with a subtle theme-matching thumb.
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 3px;
  }
}

.view-footer {
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  gap: 12px;
}

// Search field — element-ui small input is around 32px tall.
.add-item-search {
  margin-bottom: 12px;
}

// Category chips. Pill style from MEMORY.md UI patterns: radius 4px,
// padding 5/10, font 11, gap 6, nowrap. Active uses primary tint.
.add-item-chips {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  gap: 6px;
  margin-bottom: 14px;
  padding-bottom: 4px;

  // Override the global ::-webkit-scrollbar-thumb rule at
  // _sidebar-and-main-panel.scss:572 which sets a bright-green 4px border on
  // every scrollbar in the app. At 4px scrollbar height that border swallows
  // the entire thumb and renders as a solid green band.
  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border: none;
    border-radius: 2px;
  }
}

.add-item-chip {
  flex: 0 0 auto;
  border-radius: 4px;
  // Vertical padding bumped from 5 → 8 so chip height lands at ~36px — close
  // to the 44×44 mobile touch-target guideline without breaking the pill
  // aesthetic. Font size stays at 11px per the pill convention in MEMORY.md.
  padding: 8px 12px;
  font-size: 11px;
  white-space: nowrap;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;

  &:hover {
    color: #fff;
    border-color: rgba(255, 255, 255, 0.3);
  }

  &--active {
    background: rgba(225, 78, 202, 0.15);
    border-color: #e14eca;
    color: #e14eca;
  }
}

// Grid: 2 columns at drawer width, 3 above 540px (still inside drawer).
.add-item-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 16px;

  @media (min-width: 540px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.add-item-tile {
  display: flex;
  flex-direction: column;
  text-align: left;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  padding: 8px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  min-width: 0;

  &:hover:not(:disabled) {
    border-color: rgba(225, 78, 202, 0.4);
    background: rgba(255, 255, 255, 0.05);
  }

  &--selected {
    border-color: #e14eca;
    background: rgba(225, 78, 202, 0.08);
  }

  // Disabled / out-of-stock state — strike through the name + price as a
  // structural cue that doesn't depend on the floating badge being visible
  // against the tile media. 0.55 opacity keeps the name still readable.
  &--disabled {
    opacity: 0.55;
    cursor: not-allowed;

    .add-item-tile__name,
    .add-item-tile__price {
      text-decoration: line-through;
    }
  }

  &--skel {
    cursor: default;
  }

  &--custom {
    border-style: dashed;
    border-color: rgba(225, 78, 202, 0.4);
  }
}

.add-item-tile__media {
  width: 100%;
  height: 60px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.04);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 6px;
  position: relative;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &--custom {
    background: rgba(225, 78, 202, 0.08);
    color: #e14eca;
    font-size: 22px;
  }
}

.add-item-tile__placeholder {
  font-size: 22px;
  color: rgba(255, 255, 255, 0.25);
}

.add-item-tile__badge {
  position: absolute;
  top: 4px;
  right: 4px;
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 5px;
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;

  &--out {
    background: rgba(245, 54, 92, 0.85);
  }

  /* Add-on for the booked experience. Same top-right corner as out-of-stock;
     when both apply the add-on badge drops one row (markup order is out, then
     add-on). Side by side they are wider than the tile image. */
  &--addon {
    background: rgba(225, 78, 202, 0.9);
  }

  &--out + &--addon {
    top: 24px;
  }
}

.add-item-tile__name {
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;
  margin-bottom: 4px;
  // Reserve 2 lines so the meta row baseline-aligns across tiles. Without
  // this, a 1-line name produces a shorter tile and the price/qty row
  // jumps vertically when the user filters categories.
  min-height: 2.6em;

  // Two-line clamp keeps tile heights aligned without truncating mid-word.
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.add-item-tile__meta {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 11px;
}

.add-item-tile__price {
  font-weight: 600;
}

.add-item-tile__qty {
  // 0.7 floor — the qty / out-of-stock signal is load-bearing; below 0.7
  // users miss "x2 remaining" or scarcity cues at a glance.
  color: rgba(255, 255, 255, 0.7);
  font-size: 10px;
}

.add-item-empty {
  padding: 20px 0;
  text-align: center;
  font-size: 13px;
}

// Selection strip — pinned between view-body (scrolling grid) and view-footer.
// Stays visible regardless of how long the catalog scroll is. Background is
// a touch lighter than the body so the strip reads as its own surface; top
// border separates it from the grid. `flex-shrink: 0` keeps the strip from
// being squeezed by a tall body on short viewports.
.add-item-strip {
  flex: 0 0 auto;
  background: rgba(255, 255, 255, 0.02);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  // Pixel-based caps (not percentage) so a short viewport doesn't lose the
  // grid entirely. At ~600px drawer height a 50% strip leaves only ~150px
  // for grid — less than one tile row. Strip scrolls internally above cap.
  max-height: 260px;
  overflow-y: auto;

  &--tall {
    max-height: 320px;
  }

  // Shrink further on short viewports so the grid keeps a usable floor.
  @media (max-height: 700px) {
    max-height: 220px;
    &--tall {
      max-height: 260px;
    }
  }

  // Override the global green-bordered scrollbar (see _sidebar-and-main-panel.scss:572).
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 3px;
  }
}

.add-item-strip__inner {
  padding: 14px 24px;
}

.add-item-detail__name {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 10px;
}

.add-item-detail__row {
  display: flex;
  gap: 12px;
  align-items: flex-start;

  // Single-column "stack" rows (name, description) — the col must fill the
  // full row width. A flex item defaults to `flex: 0 1 auto` which collapses
  // to content width when no sibling pushes against it, leaving inputs at
  // their natural size instead of spanning the row.
  &--stack {
    margin-bottom: 10px;

    > .add-item-detail__col {
      flex: 1 1 auto;
      width: 100%;
    }
  }

  > .add-item-detail__col {
    min-width: 0;
  }

  // Stay horizontal on mobile too — stacking forced the 38px height pins on
  // price-value / total (used for baseline alignment) to become dead space,
  // and the qty stepper stretched to full width which looked oversized. The
  // 3-col row fits comfortably in the full-width mobile drawer with tightened
  // column widths.
  @media (max-width: 540px) {
    gap: 8px;
  }
}

.add-item-detail__col--qty {
  flex: 0 0 130px;

  @media (max-width: 540px) {
    flex: 0 0 110px;
  }
}

.add-item-detail__col--price {
  flex: 1 1 auto;
  min-width: 0;
}

.add-item-detail__col--total {
  flex: 0 0 90px;
  text-align: right;

  @media (max-width: 540px) {
    flex: 0 0 70px;
  }
}

.add-item-detail__label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  // 0.7 is the minimum readable label contrast on the dark theme — anything
  // dimmer reads as decoration rather than information.
  color: rgba(255, 255, 255, 0.7);
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

// Pin all input fields and the total readout to 38px so qty / price / total
// align on a single baseline. el-input-number's `small` size renders ~32px
// by default; MoneyInput renders ~38px. Pinning both keeps the row aligned
// regardless of which one is showing.
.add-item-detail__qty-input {
  width: 100%;

  ::v-deep .el-input__inner {
    height: 38px;
    line-height: 38px;
  }

  ::v-deep .el-input-number__decrease,
  ::v-deep .el-input-number__increase {
    height: 36px;
    line-height: 36px;
  }
}

.add-item-detail__col--price ::v-deep .amount-input-group {
  height: 38px;
}

.add-item-detail__col--price ::v-deep .el-input-number,
.add-item-detail__col--price ::v-deep .el-input-number .el-input,
.add-item-detail__col--price ::v-deep .el-input-number .el-input__inner {
  height: 100%;
  line-height: 1;
}

// Display state stacks price-value over the Override link so it doesn't
// compete with the value at a glance. The value itself stays pinned to 38px
// so it baseline-aligns with the qty stepper and Total in the same row.
.add-item-detail__price-display {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.add-item-detail__price-value {
  height: 38px;
  display: flex;
  align-items: center;
  font-size: 14px;
}

.add-item-detail__override-link {
  font-size: 11px;
  color: #e14eca;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  // Padding adds tap area without changing visual size; negative margin
  // keeps the link from shifting layout. Underline gives an affordance on
  // touch where hover doesn't exist.
  padding: 6px 4px;
  margin: -6px -4px;
  text-decoration: underline;

  &:hover {
    color: #d143b6;
    text-decoration: underline;
  }
}

// Matches the reset-icon treatment from EditTicketsView's pricing rows so
// the override + reset affordance reads the same across the drawer.
.add-item-detail__reset-icon {
  font-size: 10px;
}

.add-item-detail__total {
  font-size: 15px;
  font-weight: 600;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.add-item-detail__field-error {
  display: block;
  margin-top: 4px;
  color: #f5365c;
  font-size: 11px;
}

// Save-error bar sits between the strip and the footer so a failed POST is
// always visible. Full-bleed strip with a subtle tinted background.
.add-item-error-bar {
  flex: 0 0 auto;
  color: #F5365C;
  font-size: 13px;
  padding: 10px 24px;
  background: rgba(245, 54, 92, 0.1);
  border-top: 1px solid rgba(245, 54, 92, 0.2);
}
</style>

<style lang="scss">
.white-content {
  .item-picker {
    .view-footer { border-top-color: #e9ecef; }

    .add-item-chip {
      border-color: #d1d5db;
      color: #525f7f;
      &:hover { color: #344675; border-color: #9ca3af; }
      &--active { color: #e14eca; border-color: #e14eca; background: rgba(225, 78, 202, 0.08); }
    }

    .add-item-tile {
      background: #ffffff;
      border-color: #e3e3e3;
      // Bumped hover bg from #fafafa (~2% from white, invisible) to #f5f6f8
      // so the hover state actually registers on light mode.
      &:hover:not(:disabled) {
        border-color: rgba(225, 78, 202, 0.5);
        background: #f5f6f8;
      }
      // Bumped selected tint from 0.06 (nearly indistinguishable from white)
      // to 0.12 plus a 1px box-shadow that doubles the border so the active
      // tile reads at a glance.
      &--selected {
        border-color: #e14eca;
        background: rgba(225, 78, 202, 0.12);
        box-shadow: 0 0 0 1px #e14eca;
      }
      // Defensive: dashed pink border needs explicit re-declaration in case
      // the scoped block's specificity drops vs the white-content overrides.
      &--custom {
        border-color: rgba(225, 78, 202, 0.5);
        border-style: dashed;
      }
    }

    .add-item-tile__media { background: #f5f6f8; }
    .add-item-tile__placeholder { color: rgba(0, 0, 0, 0.25); }
    .add-item-tile__name { color: #344675; }
    // 0.7 floor — qty + stock signals must clear the contrast minimum in
    // both themes. #9A9A9A reads as ~0.4 against white.
    .add-item-tile__qty { color: rgba(0, 0, 0, 0.7); }

    .add-item-detail__name { color: #344675; }
    .add-item-detail__label { color: rgba(0, 0, 0, 0.7); }
    .add-item-strip {
      background: #fafafa;
      border-top-color: #e3e3e3;
    }
    // Error bar: 10% red on #fafafa reads as nearly nothing. Bump the tint
    // and the top border so a failed save is unambiguous on light too.
    .add-item-error-bar {
      color: #f5365c;
      background: rgba(245, 54, 92, 0.08);
      border-top-color: rgba(245, 54, 92, 0.3);
    }
  }
}
</style>
